import { FC } from "react";
import { Typography, useTheme } from "@mui/material";

import { Input } from "components/common/inputs/Input";
import { InputPassword } from "components/common/inputs/InputPassword";
import { PasswordPolicy } from "components/common/PasswordPolicy";
import { Checkbox } from "components/common/inputs/Checkbox";
import { Link } from "components/common/Link";

import {
  StyledInitialStep,
  StyledInitialStepAgreement,
  StyledSignUpFields,
  StyledSignUpHeadings,
} from "styles/sign-up";

interface InitialStepProps {
  listOfPolicy: {
    done: boolean;
    text: string;
  }[];
  email: string;
  password: string;
  confirmedPassword: string;
  loading?: boolean;
  validFieldErrors?: string[];
  emailErrorText?: string;
  passwordErrorText?: string;
  confirmedPasswordErrorText?: string;
  agreement: boolean;
  agreementErrorText?: string;
  handleEmailChange?: (value: string) => void;
  handlePasswordChange?: (value: string) => void;
  handleConfirmedPasswordChange?: (value: string) => void;
  checkFieldValidation?: (value: string, name: string) => () => void;
  handleAgreementChange?: (value: boolean) => void;
}

const InitialStep: FC<InitialStepProps> = ({
  email,
  password,
  confirmedPassword,
  loading,
  emailErrorText,
  passwordErrorText,
  confirmedPasswordErrorText,
  validFieldErrors,
  listOfPolicy,
  agreement,
  agreementErrorText,
  handleEmailChange,
  handlePasswordChange,
  handleConfirmedPasswordChange,
  checkFieldValidation,
  handleAgreementChange,
}) => {
  const theme = useTheme();

  return (
    <StyledInitialStep>
      <StyledSignUpHeadings>
        <Typography variant="h1" color={theme.palette.common.black}>
          Become a taster
        </Typography>
        <Typography
          variant="bodyL"
          color={theme.palette.common.black}
          paragraph
          fontWeight={300}
          marginBottom={0}
        >
          Please enter your account details. This process will require under 5
          minutes
        </Typography>
      </StyledSignUpHeadings>
      <StyledSignUpFields>
        <Input
          value={email}
          onChange={handleEmailChange}
          disabled={loading}
          onBlur={checkFieldValidation?.(email, "email")}
          error={validFieldErrors?.includes("email")}
          placeholder="Enter your email"
          label="Email address"
          errorText={emailErrorText || "Please enter an email"}
        />
        <InputPassword
          value={password}
          onChange={handlePasswordChange}
          disabled={loading}
          onBlur={checkFieldValidation?.(password, "password")}
          error={validFieldErrors?.includes("password")}
          errorText={passwordErrorText || "Please enter a password"}
          placeholder="Enter your password"
          label="Password"
        />
        <InputPassword
          value={confirmedPassword}
          onChange={handleConfirmedPasswordChange}
          placeholder="Confirm new  password"
          label="Confirm the New Password"
          errorText={
            confirmedPasswordErrorText || "Please confirm the new password"
          }
          disabled={loading}
          error={validFieldErrors?.includes("confirmedPassword")}
          onBlur={checkFieldValidation?.(
            confirmedPassword,
            "confirmedPassword"
          )}
        />
      </StyledSignUpFields>
      <PasswordPolicy listOfPolicy={listOfPolicy} />
      <StyledInitialStepAgreement>
        <Checkbox
          checked={agreement}
          disabled={loading}
          onChange={handleAgreementChange}
        />

        <Typography
          variant="bodyM"
          color={theme.palette.customGrey.dark}
          paragraph
          fontWeight={300}
          marginBottom={0}
        >
          I agree with{" "}
          <Link route="" size="sm">
            Terms and Conditions
          </Link>{" "}
          and the{" "}
          <Link route="" size="sm">
            Privacy Policy
          </Link>
        </Typography>
      </StyledInitialStepAgreement>
      <Typography
        variant="bodyM"
        color={theme.palette.red.main}
        fontWeight={300}
        margin="5px 0 10px"
        minHeight="20px"
      >
        {agreementErrorText}
      </Typography>
    </StyledInitialStep>
  );
};

export default InitialStep;
